import { CdkDrag, CdkDragDrop, CdkDragEnter, CdkDragMove, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { SnackBarService } from '@app/services/snack-bar.service';
import { DialogTarefaComponent } from '@app/components/dialog-tarefa/dialog-tarefa.component';
import { DialogTarefaEtiquetaComponent } from '@app/components/dialog-tarefa-etiqueta/dialog-tarefa-etiqueta.component';
import { DialogTarefaColunaComponent } from '@app/components/dialog-tarefa-coluna/dialog-tarefa-coluna.component';
import { DialogTarefaQuadroComponent, DialogTarefaQuadroResultado } from '@app/components/dialog-tarefa-quadro/dialog-tarefa-quadro.component';
import { ListaDadosColuna } from '@app/components/lista-dados/lista-dados.component';
import { DialogTarefasEtiquetasComponent } from '@app/components/dialog-tarefas-etiquetas/dialog-tarefas-etiquetas.component';
import { BeTarefa, BeTarefaCartao, BeTarefaColuna, BeTarefaEtiqueta, BeTarefaQuadro } from '@app/models/be-tarefa';

@Component({
  selector: 'app-tarefas',
  templateUrl: './tarefas.component.html',
  styleUrls: ['./tarefas.component.css']
})
export class TarefasComponent implements OnInit {
  public readonly atrasoArraste = { touch: 400, mouse: 0 };
  public quadros: BeTarefa[] = [];
  public quadro?: BeTarefaQuadro;
  public etiquetas: BeTarefaEtiqueta[] = [];
  public novaColuna = '';
  public novoCartao = '';
  public criandoColuna = false;
  public criandoCartao = false;
  public dialogoNovoQuadroAberto = false;
  public idColunaNovoCartao?: number;
  public idQuadroEditando?: number;
  public nomeQuadro = '';
  public adicionandoColuna = false;
  public idColunaEditandoTitulo?: number;
  public tituloColuna = '';
  public arrastandoCartao = false;
  public idCartaoMovimentacaoPendente?: number;
  public idCartaoRecemCriado?: number;
  public idColunaDestinoArraste?: number;
  public idColunaOrigemArraste?: number;
  public idCartaoArrastado?: number;
  public alturaCartaoArrastado = 0;
  private dialogoCartaoAberto = false;
  private indiceDestinoArraste?: number;
  private posicaoVerticalArraste?: number;
  private readonly colunasRecolhidas = new Set<number>();
  public filtro = '';
  public prioridadeFiltro = '';
  public vencimentoFiltro = '';
  public carregando = false;
  public modo: 'quadros' | 'etiquetas' = 'quadros';
  public readonly colunasEtiquetas: ListaDadosColuna[] = [{
    campo: 'noEtiqueta', rotulo: 'Etiqueta', tamanho: '100%', tipoExibicao: 'etiqueta',
    corValor: etiqueta => etiqueta.noCor,
    corTextoValor: etiqueta => this.corTextoEtiqueta(etiqueta.noCor)
  }];

  public constructor(
    private readonly http: HttpClient,
    private readonly dialog: MatDialog,
    private readonly snackBarService: SnackBarService
  ) { }

  public ngOnInit(): void {
    this.carregarQuadros();
    this.carregarEtiquetas();
  }

  public carregarQuadros(idParaSelecionar?: number): void {
    this.carregando = true;
    this.http.get<BeTarefa[]>('/api/Tarefas/quadros').subscribe({
      next: quadros => {
        this.quadros = quadros.filter(quadro => !quadro.blArquivado);
        const id = idParaSelecionar ?? this.quadro?.tarefa.id ?? this.quadros[0]?.id;
        if (id) this.selecionarQuadro(id);
        else {
          this.quadro = undefined;
          this.carregando = false;
        }
      },
      error: () => this.tratarErro('Não foi possível carregar os quadros.')
    });
  }

  public selecionarQuadro(id: number, focarNovoCartao = false, idCartaoAnimado?: number): void {
    this.carregando = true;
    this.http.get<BeTarefaQuadro>(`/api/Tarefas/quadros/${id}`).subscribe({
      next: quadro => {
        quadro.colunas.forEach(coluna => coluna.cartoes.forEach(cartao => cartao.dtVencimento = this.dataParaInput(cartao.dtVencimento)));
        this.quadro = quadro;
        this.restaurarColunasRecolhidas(quadro);
        this.carregando = false;
        if (idCartaoAnimado) {
          this.idCartaoRecemCriado = idCartaoAnimado;
          window.setTimeout(() => this.idCartaoRecemCriado = undefined, 650);
        }
        if (focarNovoCartao) window.setTimeout(() => document.querySelector<HTMLTextAreaElement>('.tarefas__novo-cartao-input')?.focus());
      },
      error: () => this.tratarErro('Não foi possível carregar este quadro.')
    });
  }

  public soltarQuadro(evento: CdkDragDrop<BeTarefa[]>): void {
    if (evento.previousIndex === evento.currentIndex) return;
    moveItemInArray(this.quadros, evento.previousIndex, evento.currentIndex);
    const idsQuadros = this.quadros.map(quadro => quadro.id!);
    this.http.put<BeTarefa[]>('/api/Tarefas/quadros/ordenacao', { idsQuadros }).subscribe({
      error: erro => {
        this.tratarErro('Não foi possível reordenar os quadros.', erro);
        this.carregarQuadros();
      }
    });
  }

  public iniciarNovoQuadro(): void {
    if (this.dialogoNovoQuadroAberto) return;
    this.cancelarEdicaoQuadro();
    this.dialogoNovoQuadroAberto = true;
    this.dialog.open(DialogTarefaQuadroComponent, {
      autoFocus: true,
      disableClose: true,
      width: '640px',
      maxWidth: 'calc(100vw - 32px)'
    }).afterClosed().subscribe((resultado?: DialogTarefaQuadroResultado) => {
      this.dialogoNovoQuadroAberto = false;
      if (resultado) this.criarQuadro(resultado);
    });
  }

  public modoEdicaoQuadroAtivo(): boolean {
    return this.dialogoNovoQuadroAberto || this.idQuadroEditando !== undefined;
  }

  public criarQuadro(resultado: DialogTarefaQuadroResultado): void {
    this.http.post<BeTarefa>('/api/Tarefas/quadros', resultado).subscribe({
      next: quadro => {
        this.carregarQuadros(quadro.id);
        this.sucesso('Quadro criado.');
      },
      error: erro => this.tratarErro('Não foi possível criar o quadro.', erro)
    });
  }

  public iniciarEdicaoQuadro(quadro: BeTarefa): void {
    if (!quadro.id) return;
    this.idQuadroEditando = quadro.id;
    this.nomeQuadro = quadro.noQuadro;
    window.setTimeout(() => document.querySelector<HTMLInputElement>('.tarefas__editar-quadro-input')?.focus());
  }

  public cancelarEdicaoQuadro(): void {
    this.idQuadroEditando = undefined;
    this.nomeQuadro = '';
  }

  public salvarNomeQuadro(quadro: BeTarefa): void {
    const noQuadro = this.nomeQuadro.trim();
    this.cancelarEdicaoQuadro();
    if (!quadro.id || !noQuadro || noQuadro === quadro.noQuadro) return;
    this.http.put<BeTarefa>(`/api/Tarefas/quadros/${quadro.id}`, { ...quadro, noQuadro }).subscribe({
      next: () => {
        this.carregarQuadros(quadro.id);
        this.sucesso('Quadro renomeado.');
      },
      error: erro => this.tratarErro('Não foi possível renomear o quadro.', erro)
    });
  }

  public arquivarQuadro(quadro: BeTarefa): void {
    if (!quadro.id) return;
    const proximoId = this.quadros.find(item => item.id !== quadro.id)?.id;
    this.http.put(`/api/Tarefas/quadros/${quadro.id}/arquivamento?arquivado=true`, {}).subscribe({
      next: () => {
        if (this.quadro?.tarefa.id === quadro.id) this.quadro = undefined;
        this.carregarQuadros(proximoId);
        this.sucesso('Quadro arquivado.');
      },
      error: erro => this.tratarErro('Não foi possível arquivar o quadro.', erro)
    });
  }

  public abrirDialogoCorQuadro(quadro: BeTarefa): void {
    if (!quadro.id) return;
    this.dialog.open(DialogTarefaColunaComponent, {
      autoFocus: true,
      disableClose: true,
      width: '428px',
      maxWidth: 'calc(100vw - 32px)',
      data: { noCor: quadro.noCor, titulo: 'Cor de fundo do quadro', tipo: 'quadro' }
    }).afterClosed().subscribe(noCor => {
      if (noCor === undefined || noCor === quadro.noCor) return;
      this.http.put<BeTarefa>(`/api/Tarefas/quadros/${quadro.id}`, { ...quadro, noCor }).subscribe({
        next: () => this.carregarQuadros(quadro.id),
        error: erro => this.tratarErro('Não foi possível alterar a cor do quadro.', erro)
      });
    });
  }

  public criarColuna(): void {
    const noColuna = this.novaColuna.trim();
    if (this.criandoColuna || !noColuna || !this.quadro?.tarefa.id) return;
    this.criandoColuna = true;
    this.http.post<BeTarefaColuna>(`/api/Tarefas/quadros/${this.quadro.tarefa.id}/colunas`, { noColuna }).pipe(
      finalize(() => this.criandoColuna = false)
    ).subscribe({
      next: () => {
        this.novaColuna = '';
        this.adicionandoColuna = false;
        this.selecionarQuadro(this.quadro!.tarefa.id!);
      },
      error: erro => this.tratarErro('Não foi possível criar a coluna.', erro)
    });
  }

  public abrirDialogoNovoCartao(coluna: BeTarefaColuna): void {
    if (!coluna.id) return;
    this.idColunaNovoCartao = coluna.id;
    this.novoCartao = '';
    window.setTimeout(() => document.querySelector<HTMLTextAreaElement>('.tarefas__novo-cartao-input')?.focus());
  }

  public cancelarNovoCartao(): void {
    this.idColunaNovoCartao = undefined;
    this.novoCartao = '';
  }

  public cancelarNovoCartaoSeVazio(): void {
    if (!this.novoCartao.trim()) this.cancelarNovoCartao();
  }

  public criarNovoCartao(coluna: BeTarefaColuna): void {
    const noCartao = this.novoCartao.trim();
    if (this.criandoCartao || !coluna.id || !noCartao) return;
    this.criandoCartao = true;
    this.http.post<BeTarefaCartao>(`/api/Tarefas/colunas/${coluna.id}/cartoes`, { noCartao }).pipe(
      finalize(() => this.criandoCartao = false)
    ).subscribe({
      next: cartao => {
        this.novoCartao = '';
        this.idColunaNovoCartao = coluna.id;
        this.selecionarQuadro(this.quadro!.tarefa.id!, true, cartao.id);
      },
      error: erro => this.tratarErro('Não foi possível criar a tarefa.', erro)
    });
  }

  public abrirCartao(id: number): void {
    if (this.dialogoCartaoAberto || this.dialog.openDialogs.some(dialogo => dialogo.componentInstance instanceof DialogTarefaComponent)) return;
    this.dialogoCartaoAberto = true;
    this.http.get<BeTarefaCartao>(`/api/Tarefas/cartoes/${id}`).pipe(
      finalize(() => this.dialogoCartaoAberto = false)
    ).subscribe({
      next: cartao => {
        cartao.dtVencimento = this.dataParaInput(cartao.dtVencimento);
        const copia = JSON.parse(JSON.stringify(cartao)) as BeTarefaCartao;
        this.dialog.open(DialogTarefaComponent, {
          autoFocus: true,
          disableClose: true,
          width: '860px',
          maxWidth: 'calc(100vw - 32px)',
          data: { cartao: copia, etiquetas: this.etiquetas }
        }).afterClosed().subscribe(resultado => {
          if (resultado?.arquivar) this.arquivarCartao(id);
          if (resultado?.cartao) this.salvarCartao(resultado.cartao);
        });
      },
      error: () => this.tratarErro('Não foi possível carregar a tarefa.')
    });
  }

  public selecionarModo(modo: 'quadros' | 'etiquetas'): void {
    this.modo = modo;
    if (modo === 'etiquetas') this.carregarEtiquetas();
  }

  public abrirDialogoEtiquetas(): void {
    this.dialog.open(DialogTarefasEtiquetasComponent, {
      autoFocus: true,
      disableClose: true,
      width: '760px',
      maxWidth: 'calc(100vw - 32px)',
      data: { etiquetas: this.etiquetas }
    }).afterClosed().subscribe(atualizado => {
      if (!atualizado) return;
      this.carregarEtiquetas();
      if (this.quadro?.tarefa.id) this.selecionarQuadro(this.quadro.tarefa.id);
    });
  }

  public iniciarNovaColuna(): void {
    this.adicionandoColuna = true;
    this.novaColuna = '';
    window.setTimeout(() => document.querySelector<HTMLInputElement>('.tarefas__nova-coluna-input')?.focus());
  }

  public cancelarNovaColuna(): void {
    this.adicionandoColuna = false;
    this.novaColuna = '';
  }

  public cancelarNovaColunaSeVazio(): void {
    if (!this.novaColuna.trim()) this.cancelarNovaColuna();
  }

  public alternarColunaRecolhida(coluna: BeTarefaColuna): void {
    if (!coluna.id) return;
    if (this.idColunaEditandoTitulo === coluna.id) this.cancelarEdicaoTituloColuna();
    this.colunasRecolhidas.has(coluna.id) ? this.colunasRecolhidas.delete(coluna.id) : this.colunasRecolhidas.add(coluna.id);
    this.persistirColunasRecolhidas();
  }

  public colunaEstaRecolhida(coluna: BeTarefaColuna): boolean {
    return !!coluna.id && this.colunasRecolhidas.has(coluna.id);
  }

  public iniciarEdicaoTituloColuna(coluna: BeTarefaColuna): void {
    if (this.colunaEstaRecolhida(coluna)) return;
    this.idColunaEditandoTitulo = coluna.id;
    this.tituloColuna = coluna.noColuna;
    window.setTimeout(() => document.querySelector<HTMLInputElement>('.tarefas__titulo-coluna-input')?.focus());
  }

  public salvarTituloColuna(coluna: BeTarefaColuna): void {
    const noColuna = this.tituloColuna.trim();
    this.idColunaEditandoTitulo = undefined;
    if (!coluna.id || !noColuna || noColuna === coluna.noColuna) return;
    this.http.put<BeTarefaColuna>(`/api/Tarefas/colunas/${coluna.id}`, { ...coluna, noColuna }).subscribe({
      next: () => this.selecionarQuadro(this.quadro!.tarefa.id!),
      error: erro => this.tratarErro('Não foi possível alterar o nome da lista.', erro)
    });
  }

  public cancelarEdicaoTituloColuna(): void {
    this.idColunaEditandoTitulo = undefined;
    this.tituloColuna = '';
  }

  public abrirDialogoEtiqueta(etiqueta?: BeTarefaEtiqueta): void {
    this.dialog.open(DialogTarefaEtiquetaComponent, {
      autoFocus: true,
      disableClose: true,
      width: '460px',
      maxWidth: 'calc(100vw - 32px)',
      data: { etiqueta: etiqueta ? JSON.parse(JSON.stringify(etiqueta)) : undefined }
    }).afterClosed().subscribe(resultado => {
      if (!resultado) return;
      const requisicao = resultado.id
        ? this.http.put<BeTarefaEtiqueta>(`/api/Tarefas/etiquetas/${resultado.id}`, resultado)
        : this.http.post<BeTarefaEtiqueta>('/api/Tarefas/etiquetas', resultado);
      requisicao.subscribe({
        next: () => {
          this.carregarEtiquetas();
          if (this.quadro?.tarefa.id) this.selecionarQuadro(this.quadro.tarefa.id);
          this.sucesso(resultado.id ? 'Etiqueta atualizada.' : 'Etiqueta criada.');
        },
        error: erro => this.tratarErro('NÃ£o foi possÃ­vel salvar a etiqueta.', erro)
      });
    });
  }

  public removerEtiqueta(etiqueta: BeTarefaEtiqueta): void {
    if (!etiqueta.id) return;
    this.http.delete(`/api/Tarefas/etiquetas/${etiqueta.id}`).subscribe({
      next: () => {
        this.carregarEtiquetas();
        if (this.quadro?.tarefa.id) this.selecionarQuadro(this.quadro.tarefa.id);
        this.sucesso('Etiqueta removida.');
      },
      error: erro => this.tratarErro('NÃ£o foi possÃ­vel remover a etiqueta.', erro)
    });
  }

  public soltarCartao(evento: CdkDragDrop<BeTarefaCartao[]>, colunaDestino: BeTarefaColuna): void {
    const cartao = evento.item.data as BeTarefaCartao;
    const limiteDestino = colunaDestino.cartoes.length - (evento.previousContainer === evento.container ? 1 : 0);
    const indiceDestino = Math.max(0, Math.min(this.indiceDestinoArraste ?? evento.currentIndex, limiteDestino));
    this.finalizarArrasteCartao();
    if (!evento.isPointerOverContainer || !cartao?.id || !colunaDestino.id || !this.quadro?.tarefa.id) return;
    if (evento.previousContainer === evento.container && indiceDestino === evento.previousIndex) return;
    if (evento.previousContainer.data === evento.container.data) moveItemInArray(evento.container.data, evento.previousIndex, indiceDestino);
    else transferArrayItem(evento.previousContainer.data, evento.container.data, evento.previousIndex, indiceDestino);
    this.idCartaoMovimentacaoPendente = cartao.id;
    this.http.put<BeTarefaCartao>(`/api/Tarefas/cartoes/${cartao.id}/movimentacao`, { idTarefaColunaDestino: colunaDestino.id, nrOrdemDestino: indiceDestino }).subscribe({
      next: () => {
        this.idCartaoMovimentacaoPendente = undefined;
        this.selecionarQuadro(this.quadro!.tarefa.id!);
      },
      error: erro => {
        this.idCartaoMovimentacaoPendente = undefined;
        this.tratarErro('Não foi possível mover a tarefa.', erro);
        this.selecionarQuadro(this.quadro!.tarefa.id!);
      }
    });
  }

  public iniciarArrasteCartao(cartao: BeTarefaCartao, coluna: BeTarefaColuna, arraste: CdkDrag<BeTarefaCartao>): void {
    this.arrastandoCartao = true;
    this.idCartaoArrastado = cartao.id;
    this.idColunaOrigemArraste = coluna.id;
    this.idColunaDestinoArraste = coluna.id;
    this.indiceDestinoArraste = coluna.cartoes.findIndex(item => item.id === cartao.id);
    this.alturaCartaoArrastado = arraste.getPlaceholderElement().getBoundingClientRect().height;
  }

  public finalizarArrasteCartao(): void {
    this.arrastandoCartao = false;
    this.idColunaDestinoArraste = undefined;
    this.idColunaOrigemArraste = undefined;
    this.idCartaoArrastado = undefined;
    this.indiceDestinoArraste = undefined;
    this.posicaoVerticalArraste = undefined;
  }

  public moverCartao(evento: CdkDragMove<BeTarefaCartao>): void {
    this.posicaoVerticalArraste = evento.pointerPosition.y;
    const coluna = this.quadro?.colunas.find(item => item.id === this.idColunaDestinoArraste);
    const elemento = coluna?.id ? document.getElementById(`coluna-${coluna.id}`) : null;
    if (elemento) this.indiceDestinoArraste = this.posicionarFantasma(evento.source, elemento, evento.pointerPosition.y);
  }

  public entrarZonaDestino(evento: CdkDragEnter<BeTarefaCartao[]>, coluna: BeTarefaColuna): void {
    this.idColunaDestinoArraste = coluna.id;
    if (this.posicaoVerticalArraste !== undefined) {
      this.indiceDestinoArraste = this.posicionarFantasma(evento.item, evento.container.element.nativeElement, this.posicaoVerticalArraste);
    }
  }

  public sairZonaDestino(coluna: BeTarefaColuna): void {
    if (this.idColunaDestinoArraste === coluna.id) {
      this.idColunaDestinoArraste = undefined;
      this.indiceDestinoArraste = undefined;
    }
  }

  private posicionarFantasma(arraste: CdkDrag<any>, elementoColuna: HTMLElement, posicaoVertical: number): number | undefined {
    const lista = elementoColuna.querySelector<HTMLElement>('.tarefas__cartoes');
    if (!lista) return undefined;
    const cartoes = Array.from(lista.querySelectorAll<HTMLElement>('.tarefas__cartao[data-cartao-id]:not(.cdk-drag-placeholder)'));
    const indice = cartoes.findIndex(cartao => {
      const area = cartao.getBoundingClientRect();
      return posicaoVertical < area.top + area.height / 2;
    });
    const indiceDestino = indice < 0 ? cartoes.length : indice;
    const referencia = cartoes[indiceDestino] ?? null;
    const fantasma = arraste.getPlaceholderElement();
    if (fantasma.parentElement !== lista || fantasma.nextElementSibling !== referencia) lista.insertBefore(fantasma, referencia);
    return indiceDestino;
  }

  public arquivarColuna(coluna: BeTarefaColuna): void {
    if (!coluna.id || !this.quadro?.tarefa.id) return;
    this.http.delete(`/api/Tarefas/colunas/${coluna.id}`).subscribe({
      next: () => {
        this.selecionarQuadro(this.quadro!.tarefa.id!);
        this.sucesso('Lista arquivada.');
      },
      error: erro => this.tratarErro('Não foi possível arquivar a lista.', erro)
    });
  }

  public abrirDialogoCorColuna(coluna: BeTarefaColuna): void {
    this.dialog.open(DialogTarefaColunaComponent, {
      autoFocus: true,
      disableClose: true,
      width: '428px',
      maxWidth: 'calc(100vw - 32px)',
      data: { noCor: coluna.noCor }
    }).afterClosed().subscribe(noCor => {
      if (!coluna.id || noCor === undefined || noCor === coluna.noCor) return;
      this.http.put<BeTarefaColuna>(`/api/Tarefas/colunas/${coluna.id}`, { ...coluna, noCor }).subscribe({
        next: () => this.selecionarQuadro(this.quadro!.tarefa.id!),
        error: erro => this.tratarErro('Não foi possível alterar a cor da lista.', erro)
      });
    });
  }

  public soltarColuna(evento: CdkDragDrop<BeTarefaColuna[]>): void {
    if (!this.quadro?.tarefa.id || evento.previousIndex === evento.currentIndex) return;
    moveItemInArray(evento.container.data, evento.previousIndex, evento.currentIndex);
    const idsColunas = evento.container.data.map(coluna => coluna.id!);
    this.http.put<BeTarefaColuna[]>(`/api/Tarefas/quadros/${this.quadro.tarefa.id}/colunas/ordenacao`, { idsColunas }).subscribe({
      error: erro => {
        this.tratarErro('Não foi possível reordenar as colunas.', erro);
        this.selecionarQuadro(this.quadro!.tarefa.id!);
      }
    });
  }

  public cartoesExibidos(coluna: BeTarefaColuna): BeTarefaCartao[] {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const filtro = this.filtro.trim().toLocaleLowerCase();
    return coluna.cartoes.filter(cartao => {
      if (filtro && !(cartao.noCartao + ' ' + (cartao.dsCartao ?? '')).toLocaleLowerCase().includes(filtro)) return false;
      if (this.prioridadeFiltro && cartao.noPrioridade !== this.prioridadeFiltro) return false;
      if (!this.vencimentoFiltro || !cartao.dtVencimento) return !this.vencimentoFiltro;
      const vencimento = new Date(`${cartao.dtVencimento}T00:00:00`);
      if (this.vencimentoFiltro === 'vencidas') return vencimento < hoje && !coluna.blFinalizadora;
      if (this.vencimentoFiltro === 'hoje') return vencimento.getTime() === hoje.getTime();
      return vencimento >= hoje;
    });
  }

  public estaVencida(cartao: BeTarefaCartao, coluna: BeTarefaColuna): boolean {
    if (!cartao.dtVencimento || coluna.blFinalizadora) return false;
    return new Date(`${cartao.dtVencimento}T00:00:00`) < new Date(new Date().setHours(0, 0, 0, 0));
  }

  public corTextoEtiqueta(cor: string): string {
    const valor = cor.replace('#', '');
    if (!/^[0-9a-fA-F]{6}$/.test(valor)) return '#ffffff';
    const vermelho = parseInt(valor.substring(0, 2), 16);
    const verde = parseInt(valor.substring(2, 4), 16);
    const azul = parseInt(valor.substring(4, 6), 16);
    return vermelho * 0.299 + verde * 0.587 + azul * 0.114 > 170 ? '#1f2933' : '#ffffff';
  }

  public identifiqueCartao(_: number, cartao: BeTarefaCartao): number | undefined { return cartao.id; }
  public identifiqueColuna(_: number, coluna: BeTarefaColuna): number | undefined { return coluna.id; }
  public identifiqueQuadro(_: number, quadro: BeTarefa): number | undefined { return quadro.id; }
  public idQuadroSelecionado(): number | undefined { return this.quadro?.tarefa?.id; }
  public idsListasConectadas(): string[] {
    const colunas = this.quadro?.colunas ?? [];
    return colunas.map(coluna => `coluna-${coluna.id}`);
  }

  private salvarCartao(cartao: BeTarefaCartao): void {
    this.http.put<BeTarefaCartao>(`/api/Tarefas/cartoes/${cartao.id}`, cartao).subscribe({
      next: () => {
        this.selecionarQuadro(this.quadro!.tarefa.id!);
        this.sucesso('Tarefa salva.');
      },
      error: erro => this.tratarErro('Não foi possível salvar a tarefa.', erro)
    });
  }

  private arquivarCartao(id: number): void {
    this.http.put(`/api/Tarefas/cartoes/${id}/arquivamento?arquivado=true`, {}).subscribe({
      next: () => {
        this.selecionarQuadro(this.quadro!.tarefa.id!);
        this.sucesso('Tarefa arquivada.');
      },
      error: erro => this.tratarErro('Não foi possível arquivar a tarefa.', erro)
    });
  }

  private carregarEtiquetas(): void {
    this.http.get<BeTarefaEtiqueta[]>('/api/Tarefas/etiquetas').subscribe({
      next: etiquetas => this.etiquetas = etiquetas,
      error: () => this.tratarErro('Não foi possível carregar as etiquetas.')
    });
  }

  private restaurarColunasRecolhidas(quadro: BeTarefaQuadro): void {
    this.colunasRecolhidas.clear();
    const chave = this.chaveColunasRecolhidas(quadro.tarefa.id);
    const idsColunas = new Set(quadro.colunas.map(coluna => coluna.id));
    try {
      const idsPersistidos = JSON.parse(localStorage.getItem(chave) ?? '[]') as number[];
      idsPersistidos.filter(id => idsColunas.has(id)).forEach(id => this.colunasRecolhidas.add(id));
    } catch {
      localStorage.removeItem(chave);
    }
  }

  private persistirColunasRecolhidas(): void {
    const chave = this.chaveColunasRecolhidas(this.quadro?.tarefa.id);
    localStorage.setItem(chave, JSON.stringify([...this.colunasRecolhidas]));
  }

  private chaveColunasRecolhidas(idQuadro?: number): string {
    return `tarefas.colunasRecolhidas.${idQuadro ?? 0}`;
  }

  private sucesso(mensagem: string): void { this.snackBarService.exibirSucesso(mensagem); }
  private tratarErro(mensagem: string, erro?: any): void {
    this.carregando = false;
    this.snackBarService.exibirErro(erro?.error?.message ?? mensagem);
  }
  private dataParaInput(data?: string | null): string | null { return data ? data.substring(0, 10) : null; }
}
